/**
 * Standalone list component that responds to batch:list:update:{sectionId} pubsub events.
 * Use with section-id attribute; list content goes in a child with data-batch="list".
 */
class PaginationBatchList extends HTMLElement {
  get sectionId() {
    return this.getAttribute('section-id') || this.getAttribute('data-batch-section-id') || '';
  }

  connectedCallback() {
    this.list = this.querySelector('[data-batch="list"]') || this;
    this.unsubscribe = subscribe(`batch:list:update:${this.sectionId}`, (payload) =>
      this.#handleUpdate(payload)
    );
  }

  disconnectedCallback() {
    this?.unsubscribe();
  }

  #handleUpdate({ html, direction }) {
    if (!html || !this.list) return;

    if (direction === 'next') {
      this.list.insertAdjacentHTML('beforeend', html);
    } else if (direction === 'previous') {
      this.list.insertAdjacentHTML('afterbegin', html);
      this.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    } else {
      this.list.innerHTML = html;
      this.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }
}

customElements.define('pagination-batch-list', PaginationBatchList);

/**
 * Load page batch
 */
class PaginationBatchLoader extends HTMLElement {
  sectionId = this.getAttribute('id');
  pages = parseInt(this.getAttribute('pages'));
  currentPage = parseInt(this.getAttribute('current-page'));
  pageSize = parseInt(this.getAttribute('page-size'));

  connectedCallback() {
    this.buttons = this.querySelectorAll('[data-batch-direction]');
    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.#handleBatch(button);
      });
    });

    window.onpopstate = () => {
      this.#handleBatch();
    };
  }

  #loading(state = true, button) {
    state ? this.setAttribute('loading', '') : this.removeAttribute('loading');
    button?.classList.toggle('loading', state);
  }

  #handleSearchParams() {
    if (!this.direction) return;
    this.searchParams = new URLSearchParams(document.location.search);

    let targetPage = this.currentPage;
    if (this.direction === 'next') {
      targetPage = parseInt(this.getAttribute('page-next'), 10);
    } else if (this.direction === 'previous') {
      targetPage = parseInt(this.getAttribute('page-previous'), 10);
    }

    // New page must be in pagination range, stop if not
    if (targetPage > this.pages || targetPage < 1) return;
    this.newPage = targetPage;

    this.searchParams.set('page', this.newPage);
    this.searchParams.delete('section_id');
    this.clientUrl = `${document.location.pathname}?${this.searchParams.toString()}`;

    this.searchParams.set('section_id', this.sectionId);
    this.fetchUrl = `${document.location.pathname}?${this.searchParams.toString()}`;
  }

  async #handleBatch(button) {
    this.direction = button?.dataset?.batchDirection || 'current';
    this.#loading(true, button);
    this.#handleSearchParams();
    try {
      const html = await this.#getHtml();
      this.#render(html);
      window.history.pushState({}, '', this.clientUrl);
      this.setAttribute('page-next', this.newPage < this.pages ? this.newPage + 1 : this.newPage);
      this.setAttribute('page-previous', this.newPage > 1 ? this.newPage - 1 : this.newPage);
    } catch (error) {
      console.log(error);
    } finally {
      this.#loading(false, button);
    }
  }

  async #getHtml() {
    const response = await fetch(this.fetchUrl);

    if (!response.ok) {
      throw new Error(response);
    }
    const text = await response.text();

    const html = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('[data-batch="list"]').innerHTML;

    return html;
  }

  #render(html) {
    if (!html) return;

    publish(`batch:list:update:${this.sectionId}`, {
      html,
      direction: this.direction,
      currentPage: this.newPage,
    });

    this.currentPage = this.newPage;
    this.setAttribute('current-page', this.currentPage);

    const prevBtn = this.querySelector('[data-batch-direction="previous"]');
    const nextBtn = this.querySelector('[data-batch-direction="next"]');
    if (this.currentPage === 1 && prevBtn) prevBtn.classList.add('hidden');
    if (this.currentPage === this.pages && nextBtn) nextBtn.classList.add('hidden');
  }
}

customElements.define('pagination-batch-loader', PaginationBatchLoader);
